import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeftIcon, PencilIcon, XIcon } from 'lucide-react';
import {
  Button,
  ConfirmDialog,
  DataTable,
  EmptyState,
  ErrorNotice,
  LoadingState,
  PageHeading,
  Panel,
  Pill,
  SearchInput,
} from '@bcecloud/portal-ui';
import { MessageResponse } from '@/components/ai-elements/message';
import { assistantMarkdownComponents } from '@/components/assistant/rich';
import { errorMessage, getJson, sendJson } from '@/lib/api';
import { filterRows } from '@/lib/facet-table';
import { useUrlSearch } from '@/lib/use-list-query-state';
import {
  whenLabel,
  type ScheduleRecord,
  type ScheduleRunRecord,
} from '@/pages/assistant/shared';

/** A run flattened for the search: its position in the history plus the text it matches on. */
interface RunRow {
  readonly index: number;
  readonly run: ScheduleRunRecord;
  readonly status: string;
  readonly tools: string;
  readonly output: string;
}

/** The run fields the search box matches. */
const RUN_SEARCH_FIELDS: ReadonlyArray<keyof RunRow> = ['status', 'tools', 'output'];

/** The distinct tools a run used, with their detail (e.g. email recipients). */
function runToolsLabel(run: ScheduleRunRecord): string {
  return run.actions
    .map((action) => (action.detail ? `${action.tool} ${action.detail}` : action.tool))
    .filter((label, i, all) => all.indexOf(label) === i)
    .join(', ');
}

/**
 * One schedule, read-only, at its own URL: what runs, when, and the full run
 * history — status, tool trail (including email-draft recipients), token
 * cost, and each run's delivered output rendered rich, so email preview
 * cards and event cards appear exactly as delivered. Editing lives on /edit.
 */
export function ScheduleViewPage(): React.ReactElement {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const scheduleId = useParams<{ scheduleId: string }>().scheduleId ?? '';
  const [openRun, setOpenRun] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);
  const { search, setSearch } = useUrlSearch();

  const schedules = useQuery({
    queryKey: ['assistant-schedules'],
    queryFn: ({ signal }) => getJson<ScheduleRecord[]>('/assistant/schedules', undefined, signal),
  });
  const schedule = schedules.data?.find((entry) => entry.id === scheduleId);

  const runs = useQuery({
    queryKey: ['assistant-schedule-runs', scheduleId],
    queryFn: ({ signal }) =>
      getJson<ScheduleRunRecord[]>(`/assistant/schedules/${scheduleId}/runs`, undefined, signal),
  });

  const runRows = filterRows(
    (runs.data ?? []).map((run, index) => ({ index, run, status: run.status, tools: runToolsLabel(run), output: run.output })),
    search,
    RUN_SEARCH_FIELDS,
  );

  const remove = useMutation({
    mutationFn: () => sendJson('delete', `/assistant/schedules/${scheduleId}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['assistant-schedules'] });
      navigate('/assistant/schedules');
    },
    onError: () => setDeleting(false),
  });

  if (schedules.isPending) return <LoadingState label="Loading schedule…" />;
  if (schedule === undefined) {
    return <ErrorNotice title="Schedule not found" message="It may have been deleted." />;
  }

  return (
    <section className="animate-portal-rise">
      <div className="flex items-start justify-between gap-4">
        <PageHeading
          eyebrow="AI ASSISTANT"
          title={schedule.name}
          subtitle={`Runs the ${schedule.skillName} skill${schedule.parameters ? ` — ${schedule.parameters}` : ''}`}
        />
        <span className="flex shrink-0 items-center gap-2">
          <Button variant="secondary" onClick={() => navigate('/assistant/schedules')}>
            <ArrowLeftIcon aria-hidden className="size-4" /> All schedules
          </Button>
          <Button variant="secondary" onClick={() => setDeleting(true)}>
            <XIcon aria-hidden className="size-4" /> Delete
          </Button>
          <Button onClick={() => navigate(`/assistant/schedules/${scheduleId}/edit`)}>
            <PencilIcon aria-hidden className="size-4" /> Edit
          </Button>
        </span>
      </div>

      <Panel className="p-5">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-portal-small">
          <Pill tone={schedule.enabled ? 'success' : 'neutral'}>
            {schedule.enabled ? 'Enabled' : 'Disabled'}
          </Pill>
          <span>
            <span className="font-medium text-portal-muted">Skill: </span>
            <Link
              to={`/assistant/skills/${schedule.skillScope}/${schedule.skillName}`}
              className="font-bold text-brand hover:underline"
            >
              {schedule.skillName}
            </Link>
          </span>
          <span>
            <span className="font-medium text-portal-muted">When: </span>
            {schedule.runTimes.map((row) => `${row.cron} (${row.timezone})`).join(' · ')}
          </span>
          <span>
            <span className="font-medium text-portal-muted">Next run: </span>
            {schedule.enabled ? (whenLabel(schedule.nextRunAt) ?? '—') : '—'}
          </span>
          <span>
            <span className="font-medium text-portal-muted">Last run: </span>
            {schedule.lastStatus ?? '—'}
          </span>
        </div>
      </Panel>

      <div className="mt-6">
        <h2 className="text-portal-small font-bold text-portal-ink">Run history</h2>
        <p className="mb-3 text-portal-micro text-portal-muted">
          Every run — what it did, what it produced (email drafts included), and what it cost.
        </p>
        {runs.isPending ? (
          <p className="text-portal-small text-portal-muted">Loading runs…</p>
        ) : runs.isError ? (
          <ErrorNotice inline title="Runs unavailable" message={errorMessage(runs.error)} />
        ) : (runs.data?.length ?? 0) === 0 ? (
          <Panel>
            <EmptyState
              title="No runs yet"
              message="Runs appear here after the schedule fires (within 5 minutes of its time)."
            />
          </Panel>
        ) : (
          <>
            <div className="mb-3 flex flex-wrap items-center gap-3">
              <SearchInput
                label="Search runs"
                placeholder="Status, tool, recipient or output"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className="w-full max-w-sm"
              />
            </div>
            {runRows.length === 0 ? (
              <Panel>
                <EmptyState title="No runs match" message="Try a different search." />
              </Panel>
            ) : (
              <DataTable headers={['Ran', 'Status', 'Tools used', 'Tokens', '']} minWidth={680}>
                {runRows.map(({ run, index }) => (
                  <tr key={index} className="border-t border-portal-line align-top">
                    <td className="p-3 text-portal-small">{whenLabel(run.startedAt) ?? '—'}</td>
                    <td className="p-3">
                      <Pill
                        tone={
                          run.status === 'ok' ? 'success' : run.status === 'error' ? 'danger' : 'neutral'
                        }
                      >
                        {run.status}
                      </Pill>
                    </td>
                    <td className="p-3 text-portal-small text-portal-muted">
                      {run.actions.length === 0 ? '—' : runToolsLabel(run)}
                    </td>
                    <td className="p-3 text-portal-small text-portal-muted">
                      {run.inputTokens === null
                        ? '—'
                        : `${run.inputTokens} in / ${run.outputTokens ?? 0} out`}
                    </td>
                    <td className="p-3 text-right">
                      <button
                        type="button"
                        onClick={() => setOpenRun(openRun === index ? null : index)}
                        className="cursor-pointer text-portal-small font-bold text-brand hover:underline"
                      >
                        {openRun === index ? 'Hide output' : 'View output'}
                      </button>
                    </td>
                  </tr>
                ))}
              </DataTable>
            )}
            {openRun !== null && runs.data?.[openRun] !== undefined ? (
              <Panel className="mt-3 p-5">
                {runs.data[openRun].error !== null ? (
                  <div className="mb-3">
                    <ErrorNotice inline title="Run error" message={runs.data[openRun].error} />
                  </div>
                ) : null}
                {runs.data[openRun].output === '' ? (
                  <p className="text-portal-small text-portal-muted">This run produced no output.</p>
                ) : (
                  <MessageResponse components={assistantMarkdownComponents}>
                    {runs.data[openRun].output}
                  </MessageResponse>
                )}
              </Panel>
            ) : null}
          </>
        )}
      </div>

      <ConfirmDialog
        open={deleting}
        tone="danger"
        title="Delete this schedule?"
        message={`"${schedule.name}" will stop running.`}
        confirmLabel="Delete schedule"
        onConfirm={() => remove.mutate()}
        onCancel={() => {
          if (!remove.isPending) setDeleting(false);
        }}
        pending={remove.isPending}
      />
    </section>
  );
}
